import { useEffect, useState } from "react";
import Auth from "../api/auth";
import {
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
} from "firebase/auth";
import { useDispatch } from "react-redux";
import { setTwoFAResolved } from "../features/user/UserSlice";
import { useIntl } from "react-intl";

const useTwoFactorAuth = () => {
  const dispatch = useDispatch();
  const [twoFactorAuthError, setTwoFactorAuthError] = useState(null);
  const [twoFactorAuthLoading, setTwoFactorAuthLoading] = useState(false);
  const [resendCodeLoading, setResendCodeLoading] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [twoFactorAuthCode, setTwoFactorAuthCode] = useState();
  const [twoFactorCodeModalvisible, setTwoFactorCodeModalvisible] =
    useState(false);
  const intl = useIntl();
  const handleCancel = () => {
    // Set the values back to their initial state or clear them as needed
    setTwoFactorAuthError(null);
    setVerificationId(null);
    setTwoFactorAuthCode(null);
    setTwoFactorCodeModalvisible(false);
    setTwoFactorAuthCode(null);
    // Sign the user out
    Auth.signOut();
  };

  const decodeErrorMessage = (error) => {
    const knownErrorCodes = [
      "auth/too-many-requests",
      "auth/invalid-verification-code",
      "auth/code-expired",
      "auth/wrong-password",
      "auth/invalid-email",
      "auth/invalid-action-code",
      "auth/requires-recent-login",
      "auth/user-token-expired",
    ];
    return knownErrorCodes.includes(error.code)
      ? intl.formatMessage({
          id: `app.notifications.${error.code}`,
        })
      : error.message;
  };

  useEffect(() => {
    if (twoFactorCodeModalvisible === false) {
      setTwoFactorAuthError(null);
      setTwoFactorAuthCode(null);
    }
  }, [twoFactorCodeModalvisible]);
  const initiateTwoFactorAuthentication = async (
    activeFactorId,
    authResult
  ) => {
    switch (activeFactorId) {
      case TotpMultiFactorGenerator.FACTOR_ID:
        setTwoFactorCodeModalvisible(true);

        return true;
      case PhoneMultiFactorGenerator.FACTOR_ID:
        try {
          const result = await Auth.send2FACode(
            authResult?.resolver,
            authResult?.recaptchaVerifier
          );
          setVerificationId(result.verificationId);
          setTwoFactorCodeModalvisible(true);
          return true;
        } catch (error) {
          const message = decodeErrorMessage(error);
          setTwoFactorAuthError(message);
        } finally {
          setResendCodeLoading(false);
        }
        break;
      default:
        break;
    }
    return false;
  };

  const verifyTwoFactorAuthCode = async (method, authResult) => {
    setTwoFactorAuthLoading(true);
    const totpFactor = authResult?.resolver?.hints.find(
      (factor) => factor.factorId === "totp"
    );
    const uid = totpFactor ? totpFactor.uid : null;
    try {
      await Auth[method](
        authResult?.resolver,
        method === "confirm2FATotpCode" ? uid : verificationId,
        twoFactorAuthCode
      );
      dispatch(setTwoFAResolved(true));
      setTwoFactorCodeModalvisible(false);
    } catch (error) {
      setTwoFactorAuthError(decodeErrorMessage(error));
    } finally {
      setTwoFactorAuthLoading(false);
    }
  };

  const resendTwoFactorAuthCode = async (authResult) => {
    setResendCodeLoading(true);
    try {
      const result = await Auth.send2FACode(
        authResult?.resolver,
        authResult?.recaptchaVerifier
      );
      setVerificationId(result.verificationId);
      setTwoFactorAuthError(null);
    } catch (error) {
      const message = decodeErrorMessage(error);
      setTwoFactorAuthError(message);
    } finally {
      setResendCodeLoading(false);
    }
  };

  return {
    twoFactorAuthError,
    twoFactorAuthLoading,
    resendCodeLoading,
    setTwoFactorAuthCode,
    initiateTwoFactorAuthentication,
    verifyTwoFactorAuthCode,
    resendTwoFactorAuthCode,
    twoFactorAuthCode,
    handleCancel,
    twoFactorCodeModalvisible,
  };
};

export default useTwoFactorAuth;
